// @mui
import { styled } from "@mui/material/styles";
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from "@mui/material";
// hooks
import useCountdown from "hooks/useCountdown";
// components
import Page from "./Page";
// assets
import { MaintenanceIllustration } from "assets";
import { useMemo } from "react";

// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

const CountdownContainer = styled(Card)(({ theme }) => ({
  backdropFilter: "blur(10px)",
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: theme.shadows[4],
  marginTop: theme.spacing(3),
}));

const CountdownStyle = styled("div")({
  display: "flex",
  justifyContent: "center",
  gap: 24,
  flexWrap: "wrap",
  marginTop: 16,
});

const TimeBlock = styled("div")({
  textAlign: "center",
  minWidth: 70,
  animation: "pulse 1.5s infinite ease-in-out",
  "@keyframes pulse": {
    "0%": { transform: "scale(1)", opacity: 1 },
    "50%": { transform: "scale(1.05)", opacity: 0.85 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },
});

// ----------------------------------------------------------------------

export default function MaintenanceMode({ maintenanceTime }) {
  const parsedMaintenanceTime = useMemo(() => {
    if (!maintenanceTime) return new Date(); // fallback
    return new Date(Date.parse(maintenanceTime)); // parsed as UTC
  }, [maintenanceTime]);

  const countdown = useCountdown(parsedMaintenanceTime);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Page title="System Under Maintenance" sx={{ height: 1 }}>
      <RootStyle>
        <Container maxWidth="sm">
          <Box textAlign="center">
            <Typography variant={isMobile ? "h5" : "h4"} gutterBottom fontWeight="bold">
              ⚙️ Scheduled Maintenance
            </Typography>

            <Typography sx={{ color: "text.secondary", mb: 2 }}>
              We’re currently performing essential system updates to improve performance and reliability. Thank you for your patience and understanding.
            </Typography>

            <MaintenanceIllustration
              sx={{
                my: isMobile ? 2 : 4,
                height: isMobile ? 140 : 200,
                maxWidth: "100%",
              }}
            />

            <Typography sx={{ color: "text.secondary", mb: 2 }}>
              Estimated time remaining:
            </Typography>

            <CountdownContainer>
              <CountdownStyle>
                {["days", "hours", "minutes", "seconds"].map((label) => (
                  <TimeBlock key={label}>
                    <Typography variant="h4" color="primary.main">
                      {!isNaN(countdown[label]) ? countdown[label] : "--"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {label.charAt(0).toUpperCase() + label.slice(1)}
                    </Typography>
                  </TimeBlock>
                ))}
              </CountdownStyle>
            </CountdownContainer>

            <Typography sx={{ mt: 3, color: "text.secondary", fontSize: 13 }}>
              Service is expected to resume by{" "}
              <strong>
                {new Date(maintenanceTime).toLocaleString(undefined, {
                  weekday: "long",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </strong>.
            </Typography>

            <Typography sx={{ mt: 4, color: "text.secondary", fontSize: 14 }}>
              For urgent support, contact us at{" "}
              <Box component="span" fontWeight="medium" color="text.primary">
                <a href="mailto:ict@oag.gov.so" style={{ textDecoration: 'none', color: 'inherit' }}>
                  ict@oag.gov.so
                </a>
              </Box>{" "}
              or WhatsApp{" "}
              <Box component="span" fontWeight="medium" color="text.primary">
                +252 770569563
              </Box>.
            </Typography>
          </Box>
        </Container>
      </RootStyle>
    </Page>
  );
}
